import React, { useEffect, useRef, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { Stack, xcss, Box } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { ReactionsProvider } from '@confluence/comment-context';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { usePageContentId } from '@confluence/page-context';
import { useCommentsDataState, useActiveCommentsQuery } from '@confluence/comments-data';
import {
	getOpenCommentThreads,
	LocationValue,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	width: '100%',
	minHeight: '50px',
});

const placeholderStyle = xcss({
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type CommentsQueryHookVariables = {
	pageId: string;
	skip: boolean;
};

const ObjectCommentContainerComponent = withFlags(({ flags }: WithFlagsProps) => {
	const [contentId] = usePageContentId();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);
	const [isInViewport, setIsInViewport] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [state] = useContentState();
	const pageMode = useGetPageMode();
	const isServer = useRenderServerPlaceholder();
	const { orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap } =
		useCommentsDataState();

	const { showInlineComments, showGeneralComments, replyToCommentId } = useCommentsPanelState();

	// Set up IntersectionObserver
	useEffect(() => {
		if (isServer) {
			return;
		}

		// eslint-disable-next-line no-restricted-syntax
		const observer = new IntersectionObserver(
			([entry]) => {
				const isIntersecting = entry.isIntersecting;
				setIsInViewport(isIntersecting);
			},
			{
				root: null,
				rootMargin: '100px',
				threshold: 0.1,
			},
		);

		if (wrapperRef.current) {
			observer.observe(wrapperRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [isServer]);

	// Query setup - only skip if not in viewport and not loaded yet
	const commentsQueryHook = useActiveCommentsQuery;
	const commentsQueryHookVariables: CommentsQueryHookVariables = {
		pageId: contentId || '',
		skip: !isInViewport && !hasLoaded,
	};

	const { loading, error, data } = commentsQueryHook(commentsQueryHookVariables);

	// Update loaded state when data is received
	useEffect(() => {
		if (data && !hasLoaded) {
			setHasLoaded(true);
		}
	}, [data, hasLoaded]);

	// Handle editor resize
	useEffect(() => {
		const editorTextArea = document.getElementById('ak-editor-textarea');
		if (editorTextArea) {
			const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
				if (fg('confluence_footer_comment_styling_change')) {
					const newTop = getCommentPositionTopOfSection(entries);
					if (fg('confluence_footer_comment_styling_fix_flicker')) {
						flushSync(() => {
							newTop && setCommentSectionTop(newTop);
						});
					} else {
						newTop && setCommentSectionTop(newTop);
					}
				}
			});
			resizeObserver.observe(editorTextArea);
			return () => resizeObserver.disconnect();
		}
	}, []);

	if (error) {
		return null;
	}

	const commentThreads = useMemo(() => {
		if (!hasLoaded) {
			return [];
		}

		return getOpenCommentThreads({
			commentsDataMap,
			orderedActiveAnnotationIdList,
			removedThreadsMap,
			showInlineComments,
			showGeneralComments,
			location: LocationValue.FOOTER,
		});
	}, [
		hasLoaded,
		commentsDataMap,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		showInlineComments,
		showGeneralComments,
	]);

	const hasComments = commentThreads.length > 0;

	// Show placeholder when:
	// For View mode: show when content is ready and comments are not loaded
	// For Live mode: show when in viewport and loading
	// Always show during SSR
	const shouldShowPlaceholder =
		pageMode === PageMode.VIEW ? state.isContentReady && !hasLoaded : isInViewport && loading;

	// Show comments when loaded, regardless of viewport
	const shouldShowComments = hasLoaded;

	return (
		<Box
			ref={wrapperRef}
			xcss={wrapperStyle}
			testId="object-comment-wrapper"
			style={{
				top: commentSectionTop,
			}}
		>
			{shouldShowPlaceholder && (
				<Box xcss={placeholderStyle}>
					<CommentPlaceholder showPlaceholder />
				</Box>
			)}
			{shouldShowComments && (
				<ReactionsProvider contentId={contentId ?? ''}>
					{hasComments && <ObjectCommentHeader />}
					<Stack xcss={commentPanelContainerStyles}>
						<ObjectCommentsList
							commentThreads={commentThreads}
							supportedTopLevelActions={['edit', 'resolve', 'delete']}
							flags={flags}
						/>
					</Stack>
					<Box
						xcss={[
							addCommentContainerStyles,
							!!replyToCommentId
								? addCommentContainerAnimationHiddenStyles
								: addCommentContainerAnimationShowStyles,
						]}
					>
						{!shouldShowPlaceholder && <ObjectAddCommentContainer showReactions={!hasComments} />}
					</Box>
				</ReactionsProvider>
			)}
		</Box>
	);
});

export const ObjectCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectCommentContainerComponent);
